import { useLoyaltyShopping } from '~/components/loyalty-shopping';
import { formatProductSize } from '~/utils/format-product-size';
import StrainSizes from './strain-sizes';
import { useStore } from '~/context/store-context';
import { productHeading } from '~/utils/product-strain';
import { useState, useEffect, useMemo } from 'react';
import {
  getFillerImage,
  isLightBrandLogo,
  LIGHT_LOGO_TILE,
  prioritizeProductImages,
} from '~/utils/get-filler-image';
import { isVapeProduct, DisposalNotice } from '~/utils/vape-disposal';
import { useBrandLogoCache } from '~/context/brand-logo-context';
import { useCart } from '~/context/cart-context';
import { toast } from 'react-hot-toast';
import { Link, useNavigate } from '@remix-run/react';
import { useModal } from '~/context/modal-context';
import { analytics } from '~/utils/analytics';
import ProductVariants from '~/components/product-modal/product-variants';
import ProductBundles from '~/components/product-modal/product-bundles';
import LowStockAlert from '~/components/low-stock-alert';
import { useStickyOptimized } from '~/hooks/use-sticky-optimized';
import FaqsSection from '~/routes/_layout.dispensary-locations.$location/components/faqs-section';
import ProductReviewModal from '~/components/product-review-modal';
import ProductReviews from '~/components/product-reviews';
import { useReviewCache } from '~/context/review-context';
import { parseCartError, isErrorAlreadyHandled } from '~/utils/cart-error-handler';
import { useUserReview } from '~/hooks/use-user-review';
import ProductTaxonomyLinks from '~/components/product-taxonomy-links';

import { cartFetch } from '~/utils/cart-queue';
interface Review {
  id: string;
  user_id: string;
  product_id: string;
  rating: number;
  comment: string;
  created: string;
  status: string;
  user: {
    created_at: string;
    email: string;
    raw_app_meta_data: string;
  };
}

interface Product {
  id: string;
  name: string;
  strain?: string;
  size: string;
  classification: string;
  image: string;
  primary_image?: string;
  budflow_image_url?: string;
  all_images?: string[];
  brand: string;
  category: string;
  product_description: string;
  price: number;
  salePrice?: number;
  loyaltyTierLabel?: string;
  discount?: string;
  thc: string;
  subtype: string;
  lab_results: string;
  slug: string;
  cbd?: string;
  stock?: number;
  inStock?: boolean;
  sellable_quantity?: number;
  budflow_flavors?: {
    group_id: string;
    group_name: string;
    products: string[];
  };
  budflow_mup?: {
    active: boolean;
    products: string[];
    rule_config: {
      quantity?: number;
      target_price?: number;
      discount_percent?: number;
      discount_amount?: number;
      minimum_spend?: number;
    };
    rule_id: string;
    rule_name: string;
    rule_type: string;
  };
  brand_logo_url?: string;
  treez_id?: string;
}

interface ModalProductCardProps {
  onClose: () => void;
  hideNav?: boolean;
  product: Product;
  currentStoreLocation?: {
    title: string;
    slug: string;
    locationDetails: {
      faqs?: Array<{
        question: string;
        answer: string;
      }>;
    };
  } | null;
}

interface Store {
  name: string;
  value: string;
  address: string;
  hours: string;
  openTime: string;
  closeTime: string;
}

const stores: Store[] = [
  {
    name: 'BELLFLOWER',
    value: 'catalystbellflower',
    address: '9032 Artesia Blvd, Bellflower, CA 90706',
    hours: 'Open 9am - 9pm',
    openTime: '09:00',
    closeTime: '21:00',
  },
  {
    name: 'CHERRY',
    value: 'catalyst405',
    address: '3170 Cherry Ave, Long Beach, CA 90807',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'BELMONT SHORE',
    value: 'catalystbelmont',
    address: '5227 E 2nd St, Long Beach, CA 90803',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'COSTA MESA',
    value: 'catalystcostamesa',
    address: '170 E 17th St Ste 115, Costa Mesa, CA 92627',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'DALY CITY',
    value: 'catalystdalycity',
    address: '1000 King Dr Ste 200, Daly City, CA 94015',
    hours: 'Open 10am - 9.30pm',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'EL MONTE',
    value: 'catalystelmonte',
    address: '12154 Valley Blvd, El Monte, CA 91732',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'FIGUEROA',
    value: 'catalystfigueroa',
    address: '10020 S Figueroa St, Los Angeles, CA 90003',
    hours: 'Open 10am - 9.30pm',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'FLORENCE',
    value: 'catalystflorence',
    address: '316 W Florence Avenue, Los Angeles, CA 90003',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'GROVER BEACH',
    value: 'catalystgroverbeach',
    address: '1053 Highland Way, Grover Beach, CA 93433',
    hours: 'Open 9am - 9pm',
    openTime: '09:00',
    closeTime: '21:00',
  },
  {
    name: 'HEMET',
    value: 'catalysthemet',
    address: '41007 E Florida Ave, Hemet, CA 92544',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'MARINA',
    value: 'catalystmarina',
    address: '3100 Del Monte Blvd, Marina, CA 93933',
    hours: 'Open 10am - 9.30pm',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'MID CITY',
    value: 'catalystmidcity',
    address: '3318 Venice Blvd, Los Angeles, CA 90019',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'MORENO VALLEY',
    value: 'catalystmorenovalley',
    address: '24703 Alessandro Blvd, Moreno Valley, CA 92553',
    hours: 'Open 10am - 9.30pm',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'NORMANDIE',
    value: 'catalystnormandie',
    address: '8300 S Normandie Ave, Los Angeles, CA 90044',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'OXNARD',
    value: 'catalystoxnard',
    address: '4749 S Rose Ave, Oxnard, CA 93033',
    hours: 'Open 9am - 9pm',
    openTime: '09:00',
    closeTime: '21:00',
  },
  {
    name: 'PALM DESERT',
    value: 'catalystpalmdesert',
    address: '39420 Berkey Dr, Palm Desert, CA 92211',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'PATTERSON',
    value: 'catalystpatterson',
    address: '100 W Las Palmas Ave, Patterson, CA 95363',
    hours: 'Open 10am - 9pm',
    openTime: '10:00',
    closeTime: '21:00',
  },
  {
    name: 'DOWNTOWN LONG BEACH',
    value: 'catalystpine',
    address: '433 Pine Street, Ste 500, Long Beach, CA 90802',
    hours: 'Open 10.30am - 9.30pm',
    openTime: '10:30',
    closeTime: '21:30',
  },
  {
    name: 'POMONA',
    value: 'catalystpomona',
    address: '456 E Holt Ave, Pomona, CA 91767',
    hours: 'Open 9am - 9.50pm',
    openTime: '09:00',
    closeTime: '21:50',
  },
  {
    name: 'PLANET CATALYST',
    value: 'catalystp13',
    address: '3400 W Warner Ave, Santa Ana, CA 92704',
    hours: 'Mon-Thu 9am - 9.30pm | Fri-Sun 8am - 9.30pm',
    openTime: '08:00',
    closeTime: '21:30',
  },
  {
    name: 'SANTA ANA',
    value: 'catalystsantaana',
    address: '2400 Pullman St, Santa Ana, CA 92705',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SAN BERNARDINO',
    value: 'catalystsbinland',
    address: '506 Inland Center Dr, San Bernardino, CA 92408',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SILVER LAKE',
    value: 'catalystsilverlake',
    address: '2334 Fletcher Dr, Los Angeles, CA 90039',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'STANTON',
    value: 'catalyststanton',
    address: '8130 Monroe Ave, Stanton, CA 90680',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'VAN NUYS',
    value: 'catalystvannuys',
    address: '8162 Orion Ave, Van Nuys, CA 91406',
    hours: 'Open 10am - 9.30pm',
    openTime: '10:00',
    closeTime: '21:30',
  },
  {
    name: 'WATSONVILLE',
    value: 'catalystwatsonville',
    address: '1324 W Beach St, Watsonville, CA 95076',
    hours: 'Open 10am - 9pm',
    openTime: '10:00',
    closeTime: '21:00',
  },
  {
    name: 'OC3',
    value: 'oc3',
    address: '3122 S Halladay St Unit A, Santa Ana, CA 92705',
    hours: 'Open 9am-9:30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'LANCASTER',
    value: 'catalystlancaster',
    address: '42020 4th St E, Lancaster, CA 93535',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'LYNWOOD',
    value: 'catalystlynwood',
    address: '5110 E Imperial Hwy, Lynwood, CA 90262',
    hours: 'Open 9am-9:30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'SAN DIEGO',
    value: 'catalystsandiego',
    address: '5125 Convoy St, STE 211, San Diego, CA 92111',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'HAWTHORNE',
    value: 'catalysthawthorne',
    address: '14115 Crenshaw BLVD #9, Hawthorne, CA 90250',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
  {
    name: 'ANTIOCH',
    value: 'catalystantioch',
    address: '2625 Crow Ct, Antioch, CA 94509',
    hours: 'Open 9am - 9.30pm',
    openTime: '09:00',
    closeTime: '21:30',
  },
];

// Helper function to read a cookie value.
const getCookie = (name: string) => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

export default function ModalProductCard({
  onClose,
  hideNav,
  product,
  currentStoreLocation,
}: ModalProductCardProps) {
  const [quantity, setQuantity] = useState(1);
  const { selectedStore } = useStore();
  const heading = productHeading(product);
  // Announced to screen readers when the quantity changes via +/- (see live region in the return).
  const [quantityMessage, setQuantityMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showFullLabResults, setShowFullLabResults] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Track selected image for gallery
  // Check login status synchronously on initial render to avoid showing logged-out state after hard refresh
  const [userLoggedIn, setUserLoggedIn] = useState(() => {
    // Only check on client-side
    if (typeof window === 'undefined') return false;
    const userId = getCookie('catalystUserId');
    return !!userId;
  });
  const { openCart, incrementCount } = useCart();
  const { closeProductModal, openProductModal } = useModal();
  const navigate = useNavigate();
  const [pickupStore, setPickupStore] = useState<Store | null>(null);
  const [reviewRefreshKey, setReviewRefreshKey] = useState(0); // Increment to trigger ProductReviews refetch

  // 🚀 Use review cache for better performance
  const { getReviews, isLoadingReviews, fetchReviews } = useReviewCache();
  const { getBrandLogo } = useBrandLogoCache();
  const {
    hasReviewed,
    status: reviewStatus,
    isLoading: isLoadingUserReview,
    refetch: refetchUserReview,
  } = useUserReview(product?.treez_id || product?.id);

  // Handler to refetch both top section and bottom ProductReviews component
  const handleReviewSubmitted = () => {
    refetchUserReview(); // Update top section
    setReviewRefreshKey((prev) => prev + 1); // Trigger ProductReviews to refetch
  };

  // Track if component is mounted to avoid hydration issues
  const [isMounted, setIsMounted] = useState(false);

  // Mark as mounted after hydration
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Use treez_id (global) for reviews, fallback to product.id (store-specific)
  const reviewProductId = product?.treez_id || product?.id || '';

  // Get reviews from cache
  const reviews = getReviews(reviewProductId) || [];

  // Sticky functionality for image (left) - using modal scroll container
  // The modal-wrapper is the scroll container for the modal
  const { containerRef: imageContainerRef, stickyRef: imageStickyRef } = useStickyOptimized({
    side: 'left',
    scrollContainer: '#modal-wrapper',
    stickyOffset: 12,
    minScreenWidth: 1024, // Only enable on desktop (lg breakpoint)
  });

  const handleVariantChange = (variantId: string) => {
    // Reset states when switching variants (before the new product loads)
    setQuantity(1);
    setShowFullLabResults(false);
    // Open the modal with the new variant product
    // The global modal will handle the loading state
    openProductModal(variantId);
  };

  // Determine pickup store on mount.
  useEffect(() => {
    const selectedStoreValue = getCookie('selectedStore');
    const storeFound = selectedStoreValue
      ? stores.find((s) => s.value === selectedStoreValue)
      : null;
    setPickupStore(storeFound || stores[0]);
  }, []);

  // Check login status on mount AND when product changes (modal reuse)
  // This handles cases where user logs in while modal component is already mounted
  useEffect(() => {
    const userId = getCookie('catalystUserId');
    const isLoggedIn = !!userId;
    setUserLoggedIn(isLoggedIn);
  }, [product?.id]); // Re-check when product changes (modal opens/switches products)

  // 🚀 Smart review fetching: Use ReviewProvider (only after hydration with delay)
  useEffect(() => {
    if (isMounted && reviewProductId) {
      const cachedReviews = getReviews(reviewProductId);
      if (!cachedReviews) {
        // Use requestAnimationFrame to ensure hydration is complete before fetching
        let rafId1: number;
        let rafId2: number;
        rafId1 = requestAnimationFrame(() => {
          // Double RAF to ensure we're past hydration
          rafId2 = requestAnimationFrame(() => {
            fetchReviews(reviewProductId);
          });
        });
        return () => {
          cancelAnimationFrame(rafId1);
          if (rafId2) cancelAnimationFrame(rafId2);
        };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, reviewProductId]);

  // Handle product changes (from variant switching)
  useEffect(() => {
    if (product) {
      // Reset quantity when product changes
      setQuantity(1);
      // Reset lab results expansion
      setShowFullLabResults(false);
      // Reset selected image index when product changes
      setSelectedImageIndex(0);
    }
  }, [product.id]);

  // Calculate bundle savings for a given quantity
  const getBundleSavingsForQuantity = (
    qty: number,
  ): {
    hasSavings: boolean;
    savingsText: string;
    savingsAmount: number;
    isPercentOff: boolean;
    meetsThreshold: boolean;
  } => {
    const mup = product.budflow_mup;
    if (!mup?.active) {
      return {
        hasSavings: false,
        savingsText: '',
        savingsAmount: 0,
        isPercentOff: false,
        meetsThreshold: false,
      };
    }

    const requiredQty = mup.rule_config.quantity || 1;
    const isPercentOff = mup.rule_type === 'buy_x_get_percent_off';
    const meetsThreshold = qty >= requiredQty;

    if (!meetsThreshold) {
      // Show what they could save if they add more
      const itemsNeeded = requiredQty - qty;
      if (isPercentOff && mup.rule_config.discount_percent) {
        return {
          hasSavings: false,
          savingsText: `Add ${itemsNeeded} more for ${mup.rule_config.discount_percent}% off`,
          savingsAmount: 0,
          isPercentOff,
          meetsThreshold: false,
        };
      } else if (mup.rule_config.target_price) {
        const regularPrice = (product.salePrice || product.price) * requiredQty;
        const savings = regularPrice - mup.rule_config.target_price;
        return {
          hasSavings: false,
          savingsText: `Add ${itemsNeeded} more to save $${savings.toFixed(2)}`,
          savingsAmount: 0,
          isPercentOff,
          meetsThreshold: false,
        };
      }
      return {
        hasSavings: false,
        savingsText: '',
        savingsAmount: 0,
        isPercentOff,
        meetsThreshold: false,
      };
    }

    // Calculate savings for quantities that meet or exceed threshold
    const completeBundles = Math.floor(qty / requiredQty);
    const pricePerItem = product.salePrice || product.price;
    const regularPriceForBundles = pricePerItem * (completeBundles * requiredQty);

    if (isPercentOff && mup.rule_config.discount_percent) {
      const discountPercent = mup.rule_config.discount_percent;
      const savings = (regularPriceForBundles * discountPercent) / 100;
      return {
        hasSavings: true,
        savingsText: `${discountPercent}% off`,
        savingsAmount: savings,
        isPercentOff,
        meetsThreshold: true,
      };
    } else if (mup.rule_config.target_price) {
      const bundlePrice = mup.rule_config.target_price * completeBundles;
      const savings = regularPriceForBundles - bundlePrice;
      return {
        hasSavings: true,
        savingsText: `Save $${savings.toFixed(2)}`,
        savingsAmount: savings,
        isPercentOff,
        meetsThreshold: true,
      };
    }

    return {
      hasSavings: false,
      savingsText: '',
      savingsAmount: 0,
      isPercentOff,
      meetsThreshold: false,
    };
  };

  // Get all available images using proper prioritization:
  // 1. Budflow images (if any exist, use ONLY Budflow)
  // 2. Treez images (only if no Budflow)
  // 3. Brand logo (only if no product images)
  // 4. Placeholder (final fallback)
  const allAvailableImages = useMemo(() => {
    return prioritizeProductImages({
      budflow_image_url: product.budflow_image_url,
      all_images: product.all_images,
      image: product.image,
      primary_image: product.primary_image,
      brand_logo_url: product.brand_logo_url,
      brand: product.brand,
      category: product.category,
      getBrandLogo,
    });
  }, [
    product.budflow_image_url,
    product.all_images,
    product.image,
    product.primary_image,
    product.brand_logo_url,
    product.brand,
    product.category,
    getBrandLogo,
  ]);

  // Get currently displayed image based on selected index
  const currentDisplayImage = allAvailableImages[selectedImageIndex] || allAvailableImages[0];

  // Is what we are about to render a real product photo, or a brand-logo
  // stand-in? `prioritizeProductImages` only reaches its brand-logo step when
  // every photo field was empty, so the same test the grid card uses answers it
  // here — and it has to be answered, because a logo needs different treatment
  // from a photo:
  //
  //   - `object-cover` crops and stretches a logo. Photos are square product
  //     shots and survive it; a wordmark does not.
  //   - A WHITE logo on this white figure is invisible. CAT4's is white, which
  //     is why `LIGHT_BRAND_LOGOS` exists, and the grid card already renders it
  //     on a grey tile. The modal did not, so 29 CAT4 products at Bellflower
  //     showed a card image and then an apparently empty modal.
  const hasRealPhoto = !!(
    product.primary_image ||
    product.image ||
    product.budflow_image_url ||
    (product.all_images && product.all_images.length > 0)
  );
  const showingLightLogo = !hasRealPhoto && isLightBrandLogo(product.brand);

  // Calculate average rating from reviews
  const averageRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, review) => acc + review.rating, 0);
    return Math.round((sum / reviews.length) * 10) / 10; // Round to 1 decimal place
  }, [reviews]);

  // Function to scroll to reviews section
  const scrollToReviews = () => {
    const reviewsElement = document.getElementById('product-reviews');
    const modalWrapper = document.getElementById('modal-wrapper');

    if (reviewsElement && modalWrapper) {
      // Calculate position relative to the modal container
      const modalRect = modalWrapper.getBoundingClientRect();
      const reviewsRect = reviewsElement.getBoundingClientRect();
      const currentScrollTop = modalWrapper.scrollTop;

      // Calculate the target scroll position with 20px padding
      const targetScrollTop = currentScrollTop + reviewsRect.top - modalRect.top - 20;

      modalWrapper.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    }
  };

  // Helper function to render star rating
  const renderStarRating = (rating: number, size: 'sm' | 'lg' = 'lg') => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    const starSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

    return (
      <div className="flex flex-row items-center gap-0.5">
        {[...Array(fullStars)].map((_, i) => (
          <img
            key={`full-${i}`}
            src="/icons/gold-leaf.svg"
            alt=""
            className={`${starSize} flex-none`}
          />
        ))}
        {hasHalfStar && (
          <img src="/icons/gold-leaf-half.svg" alt="" className={`${starSize} flex-none`} />
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <img
            key={`empty-${i}`}
            src="/icons/gold-leaf.svg"
            alt=""
            className={`${starSize} flex-none opacity-30`}
          />
        ))}
      </div>
    );
  };

  // Quantity controls
  const incrementQuantity = () => {
    const next = quantity + 1;
    setQuantity(next);
    setQuantityMessage(`Quantity: ${next}`);
  };
  const decrementQuantity = () => {
    if (quantity > 1) {
      const next = quantity - 1;
      setQuantity(next);
      setQuantityMessage(`Quantity: ${next}`);
    }
  };

  // Clear the announcement shortly after so it isn't re-read while navigating the modal.
  useEffect(() => {
    if (!quantityMessage) return;
    const t = setTimeout(() => setQuantityMessage(''), 1000);
    return () => clearTimeout(t);
  }, [quantityMessage]);

  const { confirmAdd } = useLoyaltyShopping();

  const handleAddToCart = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isLoading || product.inStock !== true) return;
    setIsLoading(true);
    const add = await confirmAdd({
      name: product.name,
      price: product.salePrice || product.price,
      quantity: quantity,
    });
    if (!add) {
      setIsLoading(false);
      return;
    }

    // Optimistic add — show the item in the side-cart instantly (server syncs after).
    window.dispatchEvent(
      new CustomEvent('cart:optimistic-add', {
        detail: {
          name: product.name,
          name_sanitized: (product as any).name_sanitized || product.name,
          brand: product.brand,
          category: (product as any).category ?? (product as any).category_type,
          price: product.salePrice ?? product.price,
          price_sell: product.price,
          // Match the CART's image resolution (budflow_image_url first).
          primary_image:
            (product as any).budflow_image_url ||
            product.primary_image ||
            (product as any).image ||
            '',
          onsale: product.salePrice != null && product.salePrice < product.price,
          quantity,
        },
      }),
    );
    try {
      const selectedStore = localStorage.getItem('selectedStore');
      const order_id = selectedStore
        ? localStorage.getItem(`token_${selectedStore}`) || 'default_order_id'
        : 'default_order_id';

      const payload = {
        actionType: 'ADD',
        order_id,
        product_id: product.id,
        quantity: String(quantity),
        store: selectedStore,
      };

      const response = await cartFetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const responseData = await response.clone().json();

      // Check for errors in response body (API returns 200 with errors)
      if (responseData?.error && response.ok) {
        const parsedError = parseCartError(responseData, response);
        if (parsedError.shouldShowToast) {
          toast.error(parsedError.message);
        }
        throw new Error(parsedError.message);
      }

      // Handle token expiration and regeneration
      if (responseData.token_expired && responseData.new_token && responseData.store) {
        localStorage.setItem(`token_${responseData.store}`, responseData.new_token);
        document.cookie = `token_${responseData.store}=${encodeURIComponent(
          responseData.new_token,
        )}; path=/; max-age=${60 * 60 * 24 * 365}`;

        const retryPayload = {
          actionType: 'ADD',
          order_id: responseData.new_token,
          product_id: product.id,
          quantity: String(quantity),
        };

        const retryResponse = await cartFetch('/api/cart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(retryPayload),
        });

        if (!retryResponse.ok) {
          throw new Error('Failed to add to cart after token refresh');
        }

        incrementCount(quantity);
        await retryResponse.json();
        openCart();
        onClose();
        return;
      }

      if (!response.ok) {
        console.error('Full API Response:', responseData);
        const parsedError = parseCartError(responseData, response);
        const errorMessage = parsedError.message;

        // Handle token expiration when the API doesn't return a new token
        if (
          responseData.token_expired ||
          errorMessage.includes('invalid input syntax for type uuid') ||
          errorMessage.includes('Unable to locate order with id')
        ) {
          if (selectedStore) {
            try {
              const tokenResponse = await fetch('/api/skunkworks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ store: selectedStore }),
              });

              if (tokenResponse.ok) {
                const tokenData = await tokenResponse.json();
                if (tokenData.token) {
                  localStorage.setItem(`token_${selectedStore}`, tokenData.token);
                  document.cookie = `token_${selectedStore}=${encodeURIComponent(
                    tokenData.token,
                  )}; path=/; max-age=${60 * 60 * 24 * 365}`;

                  const retryPayload = {
                    actionType: 'ADD',
                    order_id: tokenData.token,
                    product_id: product.id,
                    quantity: String(quantity),
                  };

                  const retryResponse = await cartFetch('/api/cart', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(retryPayload),
                  });

                  if (retryResponse.ok) {
                    incrementCount(quantity);
                    await retryResponse.json();
                    openCart();
                    onClose();
                    return;
                  }
                }
              }
            } catch (tokenError) {
              console.error('Error generating new token:', tokenError);
            }
          }
        }

        toast.error(errorMessage);
        throw new Error(errorMessage);
      }
      incrementCount(quantity);
      const data = await response.json();

      // 🔥 Enhanced Analytics - Both GA4 + Klaviyo tracking
      analytics.trackAddToCartComplete({
        id: product.id.toString(),
        name: product.name,
        category: product.category,
        brand: product.brand,
        price: product.salePrice || product.price,
        quantity: quantity,
      });

      openCart();
      onClose();
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      // Only show generic toast if error wasn't already handled
      if (!isErrorAlreadyHandled(error)) {
        toast.error('Failed to add item to cart. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Parse lab results from the Treez lab_results JSON array
  const parseLabResults = (labResultsString: string) => {
    if (!labResultsString || typeof labResultsString !== 'string' || !labResultsString.trim()) {
      return [];
    }
    try {
      const resultsArray = JSON.parse(labResultsString);
      return Array.isArray(resultsArray) ? resultsArray : [];
    } catch (error) {
      console.error('Error parsing lab results:', error);
      return [];
    }
  };

  // Process all lab results from Treez data
  const processLabResults = (resultsArray: any[]) => {
    const processedResults: { [key: string]: string } = {};

    resultsArray.forEach((result) => {
      const { amount, amount_type, result_type } = result;

      if (result_type && amount && amount.minimum_value) {
        const formattedAmount =
          amount_type === 'PERCENTAGE'
            ? `${parseFloat(amount.minimum_value).toFixed(2)}%`
            : `${parseFloat(amount.minimum_value).toFixed(2)}${amount_type || ''}`;

        // Normalize result type names for display
        let displayName = result_type;
        if (result_type === 'DELTA-9_THC' || result_type === 'THCA') {
          displayName = 'THC';
        } else if (result_type === 'CBDA') {
          displayName = 'CBD';
        }

        processedResults[displayName] = formattedAmount;
      }
    });

    return processedResults;
  };

  const rawLabResults = parseLabResults(product.lab_results || '[]');
  const allLabResults = processLabResults(rawLabResults);

  // Helper function to check if a value is meaningful (not 0 or empty)
  const isMeaningfulValue = (value: string | null | undefined): boolean => {
    if (!value) return false;
    const trimmed = value.toString().trim();
    if (trimmed === '' || trimmed === '0' || trimmed === '0%' || trimmed === '0.00%') return false;
    const numericValue = parseFloat(trimmed.replace('%', ''));
    return !isNaN(numericValue) && numericValue > 0;
  };

  // Primary results (only show THC and CBD if they have meaningful values)
  const primaryResults = [
    ...(isMeaningfulValue(allLabResults.THC || product.thc)
      ? [{ label: 'THC', value: allLabResults.THC || product.thc }]
      : []),
    ...(isMeaningfulValue(allLabResults.CBD || product.cbd)
      ? [{ label: 'CBD', value: allLabResults.CBD || product.cbd }]
      : []),
  ].filter(Boolean);

  // Additional lab results (everything except THC and CBD)
  const additionalResults = Object.entries(allLabResults)
    .filter(([key]) => !['THC', 'CBD'].includes(key))
    .map(([key, value]) => ({
      label: key,
      value: String(value),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Format prices for display (with $ symbol)
  const formattedPrice = product.price != null ? `$${product.price.toFixed(2)}` : 'N/A';
  const formattedSalePrice = product.salePrice != null ? `$${product.salePrice.toFixed(2)}` : null;

  const appliedDiscountName = product.salePrice != null && product.salePrice < product.price
    ? product.loyaltyTierLabel?.replace(/\s*\(\s*\d+\s+stores?\s*\)/gi, '').trim()
    : undefined;
  // Numeric prices for Schema.org (without $ symbol)
  const numericPrice = product.price != null ? product.price.toFixed(2) : null;
  const numericSalePrice = product.salePrice != null ? product.salePrice.toFixed(2) : null;

  // Memoized discount calculation for performance
  const discountPercentage = useMemo(() => {
    return product.salePrice && product.salePrice < product.price
      ? Math.round(((product.price - product.salePrice) / product.price) * 100)
      : null;
  }, [product.price, product.salePrice]);

  return (
    <>
      {/* Shared live region: announces quantity changes from either stepper (desktop/mobile). */}
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {quantityMessage}
      </span>
      <section
        className="product-card font-futura relative mx-auto flex w-full flex-col bg-white px-4 lg:p-6"
        aria-labelledby="product-title"
        aria-describedby="product-description"
      >
        {/* Product Name - Mobile Only */}
        <header className="mb-2 lg:hidden">
          <h1
            id="product-title"
            className="font-poppins text-2xl leading-8 font-semibold text-[#0F172A] normal-case"
          >
            {heading.title}
          </h1>
          {heading.subtitle && <p className="font-helvetica-neue mb-2 text-sm leading-5 text-gray-600">{heading.subtitle}</p>}

          {/* Reviews Section - Only show button for logged-in users */}
          {/* Write Review Button - Mobile, always reserve space to prevent CLS */}
          <div className={`min-h-[28px] ${!userLoggedIn ? 'invisible' : ''}`}>
            {/* Show status message only if we have confirmed review (not loading) */}
            {!isLoadingUserReview && hasReviewed && reviewStatus !== 'rejected' ? (
              <div className="font-helvetica-neue text-base leading-5 text-[#065F46]">
                {reviewStatus === 'pending' ? (
                  <span className="font-medium">Thank You! Review pending approval</span>
                ) : (
                  <span className="font-medium">Thank you for your review!</span>
                )}
              </div>
            ) : !isLoadingUserReview ? (
              /* Only show button after checking - prevents duplicate reviews after hard refresh */
              <button
                type="button"
                onClick={() => setIsReviewModalOpen(true)}
                className="font-helvetica-neue inline-flex items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                aria-label={`Leave a review for ${product.name}`}
              >
                <svg
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                  />
                </svg>
                Leave a Review
              </button>
            ) : (
              /* Loading placeholder to reserve space and prevent CLS */
              <div className="font-helvetica-neue text-base leading-5 text-transparent">
                <span className="invisible">Leave a Review</span>
              </div>
            )}
          </div>

          {/* Star Rating - Mobile Only - Always reserve space */}
          <div className="flex w-full items-center justify-between">
            <div className={`flex items-center gap-1 ${reviews.length === 0 ? 'invisible' : ''}`}>
              <span className="font-helvetica-neue flex items-center text-xl leading-[18px] text-[#181818]">
                {reviews.length > 0 ? averageRating.toFixed(1) : '0.0'}
              </span>
              <div className="flex items-center gap-1">
                {renderStarRating(reviews.length > 0 ? averageRating : 0)}
                <button
                  onClick={scrollToReviews}
                  className="font-helvetica-neue cursor-pointer text-xs leading-[18px] font-normal text-gray-600 transition-colors hover:text-[#264B9A] hover:underline focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-1 focus:outline-none"
                  aria-label={`View ${reviews.length} customer review${reviews.length !== 1 ? 's' : ''}`}
                >
                  ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                </button>
              </div>
            </div>

            <div className="flex items-center">
              <span className="font-helvetica-neue text-xs leading-4 font-normal text-black">
                {formatProductSize(product.size) || 'N/A'}
              </span>
            </div>
          </div>
        </header>

        <div className="mb-6 flex flex-col lg:flex-row lg:gap-16" ref={imageContainerRef}>
          {/* Left Column - Product Image */}
          <div className="w-full lg:w-[45%]">
            <div ref={imageStickyRef} className="mx-auto w-full max-w-[31.25rem]">
              <div key={product.id}>
                <figure
                  className={`relative aspect-square w-full max-w-[31.25rem] rounded-md border border-black/10 ${
                    showingLightLogo ? `${LIGHT_LOGO_TILE} p-8` : ''
                  }`}
                  itemProp="image"
                >
                  {discountPercentage !== null && (
                    <span className="sale absolute top-0 left-0 z-10 max-w-full rounded-tl-md rounded-br-md bg-[#ffd100] px-3 py-2 text-xs leading-5 text-black shadow-sm">
                      {appliedDiscountName ? (
                        <strong className="font-bold">{appliedDiscountName}</strong>
                      ) : (
                        <strong className="font-bold">{discountPercentage}% OFF</strong>
                      )}
                    </span>
                  )}

                  <img
                    src={currentDisplayImage}
                    alt={product.name || 'N/A'}
                    className={`h-full w-full rounded-md ${
                      hasRealPhoto ? 'object-cover' : 'object-contain'
                    }`}
                    itemProp="image"
                    loading="eager"
                    decoding="sync"
                    data-fallback-attempted="false"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      const hasAttemptedFallback =
                        target.getAttribute('data-fallback-attempted') === 'true';

                      // Prevent infinite error loops - only try fallback once
                      if (!hasAttemptedFallback) {
                        target.setAttribute('data-fallback-attempted', 'true');
                        // Use same fallback logic as prioritizeProductImages: getFillerImage
                        const fallbackImage = getFillerImage(
                          product.brand,
                          product.category,
                          getBrandLogo(product.brand),
                        );
                        target.src = fallbackImage;
                        target.alt = 'Product image - Failed to load';
                        // A logo is not a photo: stop cropping it, and keep a
                        // white one visible. Mirrors the static branch above,
                        // which cannot know a URL was going to 404.
                        target.classList.remove('object-cover');
                        target.classList.add('object-contain');
                        if (isLightBrandLogo(product.brand)) {
                          target.parentElement?.classList.add(
                            ...LIGHT_LOGO_TILE.split(' '),
                            'p-8',
                          );
                        }
                      } else {
                        // The default placeholder itself failed — show nothing
                        // rather than a broken-image icon.
                        target.style.display = 'none';
                      }
                    }}
                  />
                </figure>

                {/* Thumbnail Gallery - Shown when product has multiple images (Mobile + Desktop) */}
                {allAvailableImages.length > 1 && (
                  <div
                    className={`mt-2.5 w-full gap-2.5 pb-3 md:flex md:pb-0 ${
                      allAvailableImages.length > 5
                        ? 'overflow-x-auto scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:[scrollbar-width:thin] md:[scrollbar-color:#d1d5db_transparent] md:[&::-webkit-scrollbar]:block md:[&::-webkit-scrollbar]:h-2 md:[&::-webkit-scrollbar-thumb]:rounded-full md:[&::-webkit-scrollbar-thumb]:bg-gray-300 md:hover:[&::-webkit-scrollbar-thumb]:bg-gray-400 md:[&::-webkit-scrollbar-track]:bg-transparent'
                        : 'flex'
                    } ${
                      // Mobile: Always enable horizontal scroll with touch-friendly behavior, hide scrollbar
                      'flex snap-x snap-mandatory overflow-x-auto [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
                    }`}
                    aria-label="Product image gallery"
                    role="region"
                    aria-roledescription="carousel"
                  >
                    {allAvailableImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedImageIndex(index)}
                        className={`flex flex-shrink-0 items-center justify-center rounded-md bg-white transition-all hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffd100] focus-visible:ring-offset-2 ${
                          selectedImageIndex === index
                            ? 'border-2 border-[#264B9A]'
                            : 'border border-black/10'
                        } ${
                          // Mobile: Smaller size (80x80px for touch-friendly 44px+ targets)
                          // Desktop: Larger size (110x110px)
                          'h-20 min-h-[80px] w-20 min-w-[80px] snap-start md:h-[110px] md:min-h-[110px] md:w-[110px] md:min-w-[110px]'
                        }`}
                        aria-label={`View product image ${index + 1} of ${allAvailableImages.length}`}
                        aria-pressed={selectedImageIndex === index}
                        aria-current={selectedImageIndex === index ? 'true' : 'false'}
                        type="button"
                      >
                        <img
                          src={image}
                          alt={`Product view ${index + 1}`}
                          className="h-auto max-h-[50px] w-fit object-scale-down md:max-h-[70px]"
                          loading={index < 3 ? 'eager' : 'lazy'}
                          decoding="async"
                          data-fallback-attempted="false"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            const hasAttemptedFallback = target.getAttribute('data-fallback-attempted') === 'true';
                            
                            // Prevent infinite error loops - only try fallback once
                            if (!hasAttemptedFallback) {
                              target.setAttribute('data-fallback-attempted', 'true');
                              // Use same fallback logic as prioritizeProductImages: getFillerImage
                              const fallbackImage = getFillerImage(product.brand, product.category, getBrandLogo(product.brand));
                              target.src = fallbackImage;
                              target.alt = 'Product image - Failed to load';
                            } else {
                              // If fallback also failed, hide the image to prevent broken image icon
                              target.style.display = 'none';
                            }
                          }}
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mobile Price Section - Under image on mobile only */}
          <div className="mx-auto mt-6 w-full max-w-[31.25rem] lg:hidden">
            <div className="flex w-full flex-col items-start gap-5 bg-white py-3">
              {/* Price & Quantity Row */}
              <div className="flex w-full items-start justify-center gap-4 px-11">
                {/* Price Section - Left side */}
                <div className="flex flex-1 flex-col items-center justify-center gap-5">
                  <div className="flex flex-col items-center justify-end gap-2.5">
                    <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                      ${(product.salePrice || product.price).toFixed(2)}
                    </span>
                    {product.salePrice && (
                      <span className="font-helvetica-neue text-lg leading-6 font-medium text-[#4A5565] line-through">
                        ${product.price.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Quantity Controls - Right side */}
                <div className="flex flex-1 items-start justify-center gap-7 rounded-lg pt-2">
                  <button
                    onClick={decrementQuantity}
                    className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                    aria-label="Decrease quantity"
                  >
                    -
                  </button>
                  <span className="font-poppins text-[22px] leading-6 font-bold text-[#264B9A]">
                    {quantity}
                  </span>
                  <button
                    onClick={incrementQuantity}
                    className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>


              {/* Add to Cart Button - Full width at bottom */}
              {product.inStock === true ? (<button
                onClick={handleAddToCart}
                disabled={isLoading}
                className="font-helvetica-neue flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] px-2 py-4 text-base leading-4 font-medium text-white hover:bg-[#1e3a7a] disabled:opacity-50"
              >
                {isLoading ? 'Adding...' : 'Add To Cart'}
              </button>) : (<p role="status">Out of stock at this store</p>)}
            </div>
          </div>

          {/* Right Column - Product Details */}
          <div className="flex flex-col md:mt-4 lg:mt-0 lg:flex-1">
            {/* Product Header */}
            <div className="mb-3 flex flex-col items-start gap-3">
              {/* Product Name - Desktop Only */}
              <header className="hidden w-full lg:block">
                <h1
                  className="font-poppins pb-2 text-[32px] leading-10 font-semibold text-[#0F172A] normal-case"
                  itemProp="name"
                >
                  {heading.title}
                </h1>
          {heading.subtitle && <p className="font-helvetica-neue mb-2 text-sm leading-5 text-gray-600">{heading.subtitle}</p>}

                {/* Star Rating - Desktop Only - Always reserve space */}
                <div
                  className={`hidden w-full items-center gap-1 lg:flex ${reviews.length === 0 ? 'invisible' : ''}`}
                >
                  <span className="font-helvetica-neue flex items-center text-xl leading-[18px] text-[#181818]">
                    {reviews.length > 0 ? averageRating.toFixed(1) : '0.0'}
                  </span>
                  {renderStarRating(reviews.length > 0 ? averageRating : 0)}
                  <button
                    onClick={scrollToReviews}
                    className="font-helvetica-neue cursor-pointer text-xs leading-[18px] font-normal text-gray-600 transition-colors hover:text-[#264B9A] hover:underline focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-1 focus:outline-none"
                    aria-label={`View ${reviews.length} customer review${reviews.length !== 1 ? 's' : ''}`}
                  >
                    ({reviews.length} review{reviews.length !== 1 ? 's' : ''})
                  </button>
                </div>

                {/* Write Review Button - Desktop, always reserve space to prevent CLS */}
                <div className={`hidden min-h-[28px] lg:block ${!userLoggedIn ? 'invisible' : ''}`}>
                  {/* Show status message only if we have confirmed review (not loading) */}
                  {!isLoadingUserReview && hasReviewed && reviewStatus !== 'rejected' ? (
                    <div className="font-helvetica-neue mt-2 text-base text-[#065F46]">
                      {reviewStatus === 'pending' ? (
                        <span className="font-medium">Thank You! Review pending approval</span>
                      ) : (
                        <span className="font-medium">Thank you for your review!</span>
                      )}
                    </div>
                  ) : !isLoadingUserReview ? (
                    /* Only show button after checking - prevents duplicate reviews after hard refresh */
                    <button
                      type="button"
                      onClick={() => setIsReviewModalOpen(true)}
                      className="font-helvetica-neue mt-2 inline-flex items-center gap-2 text-base leading-5 font-medium text-[#264B9A] underline transition-colors hover:text-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                      aria-label={`Leave a review for ${product.name}`}
                    >
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                      Leave a Review
                    </button>
                  ) : (
                    /* Loading placeholder to reserve space and prevent CLS */
                    <div className="font-helvetica-neue mt-2 text-base leading-5 text-transparent">
                      <span className="invisible">Leave a Review</span>
                    </div>
                  )}
                </div>
              </header>
            </div>

            {/*
              Product Tags — each links to the page listing more like it.
              onNavigate closes the modal: it closes itself on pathname change,
              which a subtype link from the matching category page does not
              trigger, since only the query string moves.
            */}
            <div className="mb-6 flex flex-col items-start gap-3">
              <ProductTaxonomyLinks
                brand={product.brand}
                category={product.category}
                subtype={product.subtype}
                labelClassName="text-[#A8A7A7]"
                onNavigate={onClose}
              />
            </div>

            <StrainSizes product={product} store={selectedStore} onSelect={handleVariantChange} />

            {/* Price & Add to Cart Section - Desktop only, below subtype */}
            <div className="mb-6 hidden flex-col items-start gap-4 rounded-lg bg-white p-6 shadow-[0px_2px_6px_rgba(0,0,0,0.15)] lg:flex">
              {/* Price Section */}
              <div
                className="flex w-full flex-col items-center justify-center gap-5"
                itemProp="offers"
                itemScope
                itemType="https://schema.org/Offer"
              >
                <meta itemProp="priceCurrency" content="USD" />
                {numericSalePrice ? (
                  <meta itemProp="price" content={numericSalePrice} />
                ) : numericPrice ? (
                  <meta itemProp="price" content={numericPrice} />
                ) : null}
                <div className="font-helvetica-neue flex flex-row items-end justify-center gap-2.5">
                  {formattedSalePrice ? (
                    <>
                      <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                        {formattedSalePrice}
                      </span>
                      <span className="font-helvetica-neue text-lg leading-6 font-medium text-[#4A5565] line-through">
                        {formattedPrice}
                      </span>
                    </>
                  ) : (
                    <span className="font-helvetica-neue text-4xl leading-6 font-bold text-[#181818]">
                      {formattedPrice}
                    </span>
                  )}
                </div>
                  <meta itemProp="availability" content="https://schema.org/InStock" />
              </div>


              {/* Quantity Info */}
              <div className="flex w-full items-center justify-center">
                <span className="text-xs leading-4 font-normal text-black">
                  {formatProductSize(product.size) || 'N/A'}
                </span>
              </div>

              {/* Low Stock Alert */}
              <LowStockAlert
                stock={product.stock}
                sellableQuantity={product.sellable_quantity}
                className="w-full"
              />

              {/* Buttons Section */}
              <div className="flex w-full flex-col items-center gap-4">
                {/* Quantity Controls */}
                <div className="flex h-12 w-full items-center justify-center gap-7 rounded-lg p-4">
                  <button
                    onClick={decrementQuantity}
                    className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                    aria-label="Decrease quantity"
                  >
                    -
                  </button>
                  <span className="font-poppins text-[22px] leading-6 font-bold text-[#264B9A]">
                    {quantity}
                  </span>
                  <button
                    onClick={incrementQuantity}
                    className="font-poppins flex h-6 w-6 items-center justify-center text-center text-3xl text-[#264B9A] hover:bg-[#264B9A] hover:text-white"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>

                {/* Add to Cart Button */}
                {product.inStock === true ? (<button
                  onClick={handleAddToCart}
                  aria-label={`Add ${quantity} ${quantity === 1 ? 'item' : 'items'} of ${product.name} to cart`}
                  className="relative flex h-12 w-full items-center justify-center gap-2 rounded-lg border-2 border-[#264B9A] bg-[#264B9A] p-4 text-base leading-4 font-medium text-white before:absolute before:inset-0 before:min-h-[44px] before:min-w-[44px] before:content-[''] hover:bg-[#1e3a7a] focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
                >
                  {isLoading ? 'Adding...' : 'Add To Cart'}
                </button>) : (<p role="status">Out of stock at this store</p>)}
              </div>
            </div>

            {/* Product Variants Section */}
            {product.budflow_flavors && (
              <div className="mb-2">
                <ProductVariants
                  currentProductId={product.id}
                  currentProductName={product.name}
                  budflowFlavors={product.budflow_flavors}
                  onVariantChange={handleVariantChange}
                />
              </div>
            )}

            {/* Multi-Unit Pricing Bundle Section */}
            <div className="mb-4">
              <ProductBundles
                currentProductId={product.id}
                currentProductPrice={product.salePrice || product.price}
                originalPrice={product.price}
                budflowMup={product.budflow_mup}
                onBundleProductClick={handleVariantChange}
              />
            </div>

            {/* Product Description Accordion */}
            {product.product_description && (
              <div className="mb-6 flex flex-col items-start gap-[14px] bg-white">
                <div className="flex w-full items-center gap-8 pr-2">
                  <h3 className="font-helvetica-neue text-xl font-medium text-black">
                    Product Description
                  </h3>
                </div>
                <div className="flex w-full flex-col items-center gap-[6px]">
                  <div className="flex w-full flex-col items-start gap-[14px] pb-2.5">
                    <div
                      id="product-description"
                      className="font-helvetica-neue w-full text-base leading-6 font-light tracking-[0.02em] text-black"
                      itemProp="description"
                    >
                      {product.product_description}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Disposal Notice - vape products only */}
            {isVapeProduct(product) && <DisposalNotice />}

            {/* Lab Results Accordion */}
            <div className="mb-6 flex flex-col items-start gap-4 bg-white pt-6">
              <div className="flex w-full flex-col gap-2">
                <div className="flex w-full items-center gap-8 pr-2">
                  <h3 className="font-helvetica-neue text-center text-xl font-medium text-black">
                    Lab Results
                  </h3>
                </div>
                {/* Batch Variation Disclaimer */}
                <p className="font-helvetica-neue text-sm leading-5 font-normal text-[#6B7280] italic">
                  Lab results may vary by batch. Actual product may differ from displayed values.
                </p>
              </div>
              <div className="flex w-full flex-col items-start gap-[6px]">
                <div className="flex w-full flex-col items-start pb-3">
                  {/* Primary Lab Results - THC and CBD */}
                  <div className="flex w-full">
                    {primaryResults.map((result, index) => (
                      <div
                        key={result.label}
                        className={`flex flex-1 items-start gap-2.5 p-2 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
                      >
                        <span className="font-helvetica-neue text-sm leading-[17px] font-medium text-black">
                          {result.label}:
                        </span>
                        <span className="font-helvetica-neue flex-1 text-sm leading-[17px] font-normal text-black">
                          {result.value}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Additional Lab Results - Shown when expanded */}
                  {showFullLabResults && additionalResults.length > 0 && (
                    <div className="mt-2 flex w-full flex-col">
                      {additionalResults.map((result, index) => (
                        <div
                          key={result.label}
                          className={`flex w-full items-start gap-2.5 p-2 ${index % 2 === 0 ? 'bg-[#F4F4F4]' : 'bg-white'}`}
                        >
                          <span className="font-helvetica-neue text-sm leading-[17px] font-medium text-black">
                            {result.label}:
                          </span>
                          <span className="font-helvetica-neue flex-1 text-sm leading-[17px] font-normal text-black">
                            {result.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Toggle Button - Only show if there are additional results */}
                {additionalResults.length > 0 && (
                  <button
                    onClick={() => setShowFullLabResults(!showFullLabResults)}
                    className="flex w-full items-center justify-center gap-2 bg-white py-1 pr-2 transition-colors hover:bg-gray-50"
                  >
                    <span className="font-helvetica-neue text-base leading-5 font-medium text-[#264B9A]">
                      {showFullLabResults ? 'Show Less' : 'Read Full List'}
                    </span>
                    <div className="h-[14px] w-[14px]">
                      {showFullLabResults ? (
                        // Chevron Up
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path
                            d="M3.5 8.75L7 5.25L10.5 8.75"
                            stroke="#264B9A"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : (
                        // Chevron Down
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path
                            d="M3.5 5.25L7 8.75L10.5 5.25"
                            stroke="#264B9A"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </button>
                )}
              </div>
              <div className="h-0.5 w-full bg-[#EBEBE6]"></div>
            </div>

            {/* Product Reviews Section */}
            <div className="mb-6 w-full">
              <ProductReviews
                productId={reviewProductId}
                onWriteReviewClick={() => setIsReviewModalOpen(true)}
                isLoggedIn={userLoggedIn}
                refreshKey={reviewRefreshKey}
                hasReviewed={hasReviewed}
                reviewStatus={reviewStatus}
                isLoadingUserReview={isLoadingUserReview}
              />
            </div>

            {/* Store FAQs Section */}
            {currentStoreLocation?.locationDetails?.faqs &&
              currentStoreLocation.locationDetails.faqs.length > 0 && (
                <div className="mb-6 w-full">
                  <div className="w-full [&>section]:w-full [&>section>div]:w-full [&>section>div]:max-w-none">
                    <FaqsSection
                      faqs={currentStoreLocation.locationDetails.faqs}
                      locationName={currentStoreLocation.title}
                      showLocationSpecificContent={false}
                    />
                  </div>
                </div>
              )}
          </div>
        </div>
      </section>

      {/* Product Review Modal */}
      <ProductReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        product={{
          id: product.id,
          treezId: product.treez_id,
          name: product.name,
          image:
            product.image ||
            getFillerImage(product.brand, product.category, getBrandLogo(product.brand)),
          brand: product.brand,
        }}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </>
  );
}
